import bbox from '@turf/bbox';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAppStore } from '@/store/appStore';
import type {
  MapParcel,
  MapperNavigatorTab,
  MapperSavedFarm,
  MapperWorkspaceGroup,
  MapperWorkspaceSnapshot,
  MapperWorkspaceViewState,
} from './types';

const DEFAULT_WORKSPACE_KEY = '__mapper_default__';

interface MapperWorkspaceRecord extends MapperWorkspaceSnapshot {
  dirty: boolean;
}

interface MapperWorkspaceState {
  byAppId: Record<string, MapperWorkspaceRecord>;
  hydrateWorkspace: (appId: string | null | undefined, snapshot: MapperWorkspaceSnapshot | null | undefined) => void;
  createGroup: (appId: string | null | undefined, name: string) => string | null;
  renameGroup: (appId: string | null | undefined, groupId: string, name: string) => void;
  deleteGroup: (appId: string | null | undefined, groupId: string) => void;
  assignFarmToGroup: (appId: string | null | undefined, parcelId: string, groupId: string | null) => void;
  saveFarm: (appId: string | null | undefined, parcel: MapParcel) => void;
  syncSavedFarm: (appId: string | null | undefined, parcel: MapParcel) => void;
  removeFarm: (appId: string | null | undefined, parcelId: string) => void;
  setActiveFarm: (appId: string | null | undefined, parcelId: string | null, entityId?: string | null, groupId?: string | null) => void;
  setActiveFarmGroup: (appId: string | null | undefined, groupId: string | null, entityId?: string | null) => void;
  setActiveEntityId: (appId: string | null | undefined, entityId: string | null) => void;
  setNavigatorTab: (appId: string | null | undefined, tab: MapperNavigatorTab) => void;
  updateView: (appId: string | null | undefined, patch: MapperWorkspaceViewState) => void;
  markSaved: (appId: string | null | undefined, savedAt?: string | null) => void;
  clearWorkspace: (appId: string | null | undefined) => void;
}

function scopeAppId(appId: string | null | undefined): string {
  return String(appId || DEFAULT_WORKSPACE_KEY);
}

function emptyWorkspace(): MapperWorkspaceRecord {
  return {
    version: 1,
    saved_farms: [],
    workspace_groups: [],
    active_farm_id: null,
    active_farm_group_id: null,
    active_entity_id: null,
    navigator_tab: 'farms',
    view: {},
    saved_at: null,
    dirty: false,
  };
}

function farmSortName(farm: Pick<MapperSavedFarm, 'feature_name' | 'farm_name' | 'external_id' | 'sg_number' | 'erf_number' | 'id'>): string {
  return String(
    farm.feature_name
    || farm.farm_name
    || farm.external_id
    || farm.sg_number
    || farm.erf_number
    || farm.id
    || ''
  ).trim().toLowerCase();
}

function serializeFarm(parcel: MapParcel, previousFarm?: MapperSavedFarm | null): MapperSavedFarm {
  const now = new Date().toISOString();
  let bounds: [number, number, number, number] | null = null;
  try {
    bounds = bbox({ type: 'Feature', geometry: parcel.geometry, properties: {} }) as [number, number, number, number];
  } catch {
    bounds = null;
  }

  return {
    id: parcel.id,
    feature_name: parcel.feature_name ?? null,
    external_id: parcel.external_id ?? null,
    sg_number: parcel.sg_number ?? null,
    erf_number: parcel.erf_number ?? null,
    portion: parcel.portion ?? null,
    farm_name: parcel.farm_name ?? null,
    province: parcel.province ?? null,
    municipality: parcel.municipality ?? null,
    local_municipality: parcel.local_municipality ?? null,
    district_municipality: parcel.district_municipality ?? null,
    country_code: parcel.country_code ?? null,
    admin_level_1: parcel.admin_level_1 ?? null,
    admin_level_2: parcel.admin_level_2 ?? null,
    registry_type: parcel.registry_type ?? null,
    source_adapter: parcel.source_adapter ?? null,
    source_pack_id: parcel.source_pack_id ?? null,
    natural_key: parcel.natural_key ?? null,
    local_identifiers: parcel.local_identifiers ?? {},
    land_category: parcel.land_category,
    land_type: parcel.land_type,
    geometry: parcel.geometry,
    bounds,
    area_ha: parcel.area_ha,
    linked_entity_id: parcel.linked_entity_id,
    workspace_group_id: null,
    active_template_ids: parcel.active_template_ids ?? [],
    source: parcel.source,
    saved_at: previousFarm?.saved_at || now,
    updated_at: parcel.metadata?.updated_at || previousFarm?.updated_at || now,
  };
}

function areSavedFarmsEquivalent(a: MapperSavedFarm | null | undefined, b: MapperSavedFarm | null | undefined): boolean {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function areViewStatesEquivalent(a: MapperWorkspaceViewState | null | undefined, b: MapperWorkspaceViewState | null | undefined): boolean {
  return (a?.bbox || undefined) === (b?.bbox || undefined)
    && a?.zoom === b?.zoom
    && a?.show_boundaries === b?.show_boundaries;
}

function normalizeSnapshot(snapshot: MapperWorkspaceSnapshot | null | undefined): MapperWorkspaceRecord {
  const base = emptyWorkspace();
  if (!snapshot) return base;
  return {
    ...base,
    version: 1,
    saved_farms: Array.isArray(snapshot.saved_farms) ? snapshot.saved_farms : [],
    workspace_groups: Array.isArray(snapshot.workspace_groups) ? snapshot.workspace_groups : [],
    active_farm_id: snapshot.active_farm_id || null,
    active_farm_group_id: snapshot.active_farm_group_id || null,
    active_entity_id: snapshot.active_entity_id || null,
    navigator_tab: snapshot.navigator_tab === 'fields' || snapshot.navigator_tab === 'layers'
      ? snapshot.navigator_tab
      : 'farms',
    view: snapshot.view || {},
    saved_at: snapshot.saved_at || null,
    dirty: false,
  };
}

function withWorkspace(
  state: MapperWorkspaceState,
  appId: string | null | undefined,
  updater: (current: MapperWorkspaceRecord) => MapperWorkspaceRecord
) {
  const scoped = scopeAppId(appId);
  const current = state.byAppId[scoped] || emptyWorkspace();
  const next = updater(current);
  if (next === current) return state;
  return {
    byAppId: {
      ...state.byAppId,
      [scoped]: next,
    },
  };
}

export const useMapperWorkspaceStore = create<MapperWorkspaceState>()(
  persist(
    (set) => ({
      byAppId: {},
      hydrateWorkspace: (appId, snapshot) => set((state) => withWorkspace(state, appId, () => normalizeSnapshot(snapshot))),
      createGroup: (appId, name) => {
        const trimmed = name.trim();
        if (!trimmed) return null;
        const groupId = `group:${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}:${Date.now()}`;
        set((state) => withWorkspace(state, appId, (current) => ({
          ...current,
          workspace_groups: [
            ...(current.workspace_groups || []),
            { id: groupId, name: trimmed, color: null, created_at: new Date().toISOString() },
          ].sort((a, b) => a.name.localeCompare(b.name)),
          dirty: true,
        })));
        return groupId;
      },
      renameGroup: (appId, groupId, name) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        workspace_groups: (current.workspace_groups || []).map((group) => (
          group.id === groupId ? { ...group, name: name.trim() || group.name } : group
        )),
        dirty: true,
      }))),
      deleteGroup: (appId, groupId) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        workspace_groups: (current.workspace_groups || []).filter((group) => group.id !== groupId),
        saved_farms: current.saved_farms.map((farm) => (
          farm.workspace_group_id === groupId ? { ...farm, workspace_group_id: null } : farm
        )),
        dirty: true,
      }))),
      assignFarmToGroup: (appId, parcelId, groupId) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        saved_farms: current.saved_farms.map((farm) => (
          farm.id === parcelId ? { ...farm, workspace_group_id: groupId || null } : farm
        )),
        dirty: true,
      }))),
      saveFarm: (appId, parcel) => set((state) => withWorkspace(state, appId, (current) => {
        const existingFarm = current.saved_farms.find((farm) => farm.id === parcel.id);
        const savedFarm = serializeFarm(parcel, existingFarm);
        const existing = current.saved_farms.filter((farm) => farm.id !== parcel.id);
        return {
          ...current,
          saved_farms: [{ ...savedFarm, workspace_group_id: existingFarm?.workspace_group_id || null }, ...existing]
            .sort((a, b) => farmSortName(a).localeCompare(farmSortName(b))),
          active_farm_id: parcel.id,
          active_farm_group_id: current.active_farm_group_id || null,
          active_entity_id: parcel.linked_entity_id || current.active_entity_id || null,
          dirty: true,
        };
      })),
      syncSavedFarm: (appId, parcel) => set((state) => withWorkspace(state, appId, (current) => {
        const existingFarm = current.saved_farms.find((farm) => farm.id === parcel.id);
        if (!existingFarm) return current;
        const savedFarm = {
          ...serializeFarm(parcel, existingFarm),
          workspace_group_id: existingFarm.workspace_group_id || null,
        };
        if (areSavedFarmsEquivalent(existingFarm, savedFarm)) return current;
        return {
          ...current,
          saved_farms: current.saved_farms
            .map((farm) => (farm.id === parcel.id ? savedFarm : farm))
            .sort((a, b) => farmSortName(a).localeCompare(farmSortName(b))),
          active_entity_id: current.active_farm_id === parcel.id
            ? (parcel.linked_entity_id || null)
            : current.active_entity_id,
          dirty: true,
        };
      })),
      removeFarm: (appId, parcelId) => set((state) => withWorkspace(state, appId, (current) => {
        const nextFarms = current.saved_farms.filter((farm) => farm.id !== parcelId);
        const nextActiveFarmId = current.active_farm_id === parcelId ? (nextFarms[0]?.id || null) : current.active_farm_id;
        const nextActiveEntityId = nextActiveFarmId
          ? nextFarms.find((farm) => farm.id === nextActiveFarmId)?.linked_entity_id || null
          : null;
        return {
          ...current,
          saved_farms: nextFarms,
          active_farm_id: nextActiveFarmId,
          active_farm_group_id: current.active_farm_group_id || null,
          active_entity_id: nextActiveEntityId,
          dirty: true,
        };
      })),
      setActiveFarm: (appId, parcelId, entityId, groupId) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        active_farm_id: parcelId || null,
        active_farm_group_id: groupId !== undefined ? (groupId || null) : current.active_farm_group_id || null,
        active_entity_id: entityId !== undefined
          ? (entityId || null)
          : (parcelId ? current.saved_farms.find((farm) => farm.id === parcelId)?.linked_entity_id || null : current.active_entity_id),
        dirty: true,
      }))),
      setActiveFarmGroup: (appId, groupId, entityId) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        active_farm_group_id: groupId || null,
        active_entity_id: entityId !== undefined ? (entityId || null) : current.active_entity_id,
        dirty: true,
      }))),
      setActiveEntityId: (appId, entityId) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        active_entity_id: entityId || null,
        dirty: true,
      }))),
      setNavigatorTab: (appId, tab) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        navigator_tab: tab,
        dirty: true,
      }))),
      updateView: (appId, patch) => set((state) => withWorkspace(state, appId, (current) => {
        const nextView = {
          ...(current.view || {}),
          ...patch,
        };
        if (areViewStatesEquivalent(current.view, nextView)) return current;
        return {
          ...current,
          view: nextView,
        };
      })),
      markSaved: (appId, savedAt) => set((state) => withWorkspace(state, appId, (current) => ({
        ...current,
        saved_at: savedAt || new Date().toISOString(),
        dirty: false,
      }))),
      clearWorkspace: (appId) => set((state) => withWorkspace(state, appId, () => emptyWorkspace())),
    }),
    {
      name: 'mapper-workspaces-v1',
      partialize: (state) => ({ byAppId: state.byAppId }),
    }
  )
);

export function savedFarmToParcel(farm: MapperSavedFarm): MapParcel {
  return {
    id: farm.id,
    tenant_id: 'workspace',
    feature_name: farm.feature_name ?? null,
    external_id: farm.external_id ?? null,
    sg_number: farm.sg_number ?? null,
    erf_number: farm.erf_number ?? null,
    portion: farm.portion ?? null,
    farm_name: farm.farm_name ?? null,
    province: farm.province ?? null,
    municipality: farm.municipality ?? null,
    local_municipality: farm.local_municipality ?? null,
    district_municipality: farm.district_municipality ?? null,
    country_code: farm.country_code ?? null,
    admin_level_1: farm.admin_level_1 ?? null,
    admin_level_2: farm.admin_level_2 ?? null,
    registry_type: farm.registry_type ?? null,
    source_adapter: farm.source_adapter ?? null,
    source_pack_id: farm.source_pack_id ?? null,
    natural_key: farm.natural_key ?? null,
    local_identifiers: farm.local_identifiers ?? {},
    land_category: farm.land_category ?? null,
    land_type: farm.land_type ?? null,
    geometry: farm.geometry,
    area_ha: farm.area_ha,
    linked_entity_id: farm.linked_entity_id,
    active_template_ids: farm.active_template_ids,
    source: farm.source,
    metadata: {
      created_at: farm.saved_at,
      updated_at: farm.updated_at,
      created_by: 'mapper-workspace',
    },
  };
}

export function useMapperWorkspace() {
  const appId = useAppStore((state) => state.appId);
  const scopedAppId = scopeAppId(appId);
  const emptyScopedWorkspace = useMemo(() => emptyWorkspace(), []);
  const workspace = useMapperWorkspaceStore(
    useCallback(
      (state: MapperWorkspaceState) => state.byAppId[scopedAppId] || emptyScopedWorkspace,
      [emptyScopedWorkspace, scopedAppId]
    )
  );

  const hydrateWorkspace = useCallback(
    (snapshot: MapperWorkspaceSnapshot | null | undefined) => {
      useMapperWorkspaceStore.getState().hydrateWorkspace(appId, snapshot);
    },
    [appId]
  );
  const createGroup = useCallback((name: string) => useMapperWorkspaceStore.getState().createGroup(appId, name), [appId]);
  const renameGroup = useCallback((groupId: string, name: string) => useMapperWorkspaceStore.getState().renameGroup(appId, groupId, name), [appId]);
  const deleteGroup = useCallback((groupId: string) => useMapperWorkspaceStore.getState().deleteGroup(appId, groupId), [appId]);
  const assignFarmToGroup = useCallback(
    (parcelId: string, groupId: string | null) => useMapperWorkspaceStore.getState().assignFarmToGroup(appId, parcelId, groupId),
    [appId]
  );
  const saveFarm = useCallback((parcel: MapParcel) => useMapperWorkspaceStore.getState().saveFarm(appId, parcel), [appId]);
  const syncSavedFarm = useCallback((parcel: MapParcel) => useMapperWorkspaceStore.getState().syncSavedFarm(appId, parcel), [appId]);
  const removeFarm = useCallback((parcelId: string) => useMapperWorkspaceStore.getState().removeFarm(appId, parcelId), [appId]);
  const setActiveFarm = useCallback(
    (parcelId: string | null, entityId?: string | null, groupId?: string | null) => useMapperWorkspaceStore.getState().setActiveFarm(appId, parcelId, entityId, groupId),
    [appId]
  );
  const setActiveFarmGroup = useCallback(
    (groupId: string | null, entityId?: string | null) => useMapperWorkspaceStore.getState().setActiveFarmGroup(appId, groupId, entityId),
    [appId]
  );
  const setActiveEntityId = useCallback((entityId: string | null) => useMapperWorkspaceStore.getState().setActiveEntityId(appId, entityId), [appId]);
  const setNavigatorTab = useCallback((tab: MapperNavigatorTab) => useMapperWorkspaceStore.getState().setNavigatorTab(appId, tab), [appId]);
  const updateView = useCallback((patch: MapperWorkspaceViewState) => useMapperWorkspaceStore.getState().updateView(appId, patch), [appId]);
  const markSaved = useCallback((savedAt?: string | null) => useMapperWorkspaceStore.getState().markSaved(appId, savedAt), [appId]);
  const clearWorkspace = useCallback(() => useMapperWorkspaceStore.getState().clearWorkspace(appId), [appId]);
  const hasSavedFarm = useCallback(
    (parcelId: string | null | undefined) => !!parcelId && workspace.saved_farms.some((farm) => farm.id === parcelId),
    [workspace.saved_farms]
  );
  const buildSnapshot = useCallback((): MapperWorkspaceSnapshot => {
    const current = useMapperWorkspaceStore.getState().byAppId[scopedAppId] || emptyScopedWorkspace;
    return {
      version: 1,
      saved_farms: current.saved_farms,
      workspace_groups: current.workspace_groups || [],
      active_farm_id: current.active_farm_id,
      active_farm_group_id: current.active_farm_group_id || null,
      active_entity_id: current.active_entity_id,
      navigator_tab: current.navigator_tab,
      view: current.view,
      saved_at: current.saved_at || null,
    };
  }, [emptyScopedWorkspace, scopedAppId]);

  return useMemo(() => ({
    appId,
    workspace,
    hydrateWorkspace,
    createGroup,
    renameGroup,
    deleteGroup,
    assignFarmToGroup,
    saveFarm,
    syncSavedFarm,
    removeFarm,
    setActiveFarm,
    setActiveFarmGroup,
    setActiveEntityId,
    setNavigatorTab,
    updateView,
    markSaved,
    clearWorkspace,
    hasSavedFarm,
    buildSnapshot,
  }), [
    appId,
    workspace,
    hydrateWorkspace,
    createGroup,
    renameGroup,
    deleteGroup,
    assignFarmToGroup,
    saveFarm,
    syncSavedFarm,
    removeFarm,
    setActiveFarm,
    setActiveFarmGroup,
    setActiveEntityId,
    setNavigatorTab,
    updateView,
    markSaved,
    clearWorkspace,
    hasSavedFarm,
    buildSnapshot,
  ]);
}
